import type { WalletBalance } from '../../../utils/Blockchains';
import type { WalletDetailItemProps, WalletDetailItemType } from '../WalletDetails.types';
import type { Asset } from 'rango-types';
import type { ReactElement } from 'react';

import { SOCIAL_WALLET_TYPE, useSocialWallet } from '@rango-dev/internal-social-wallet';
import { Callout, Flex, Grid, IconButton, Skeleton, Text, Tooltip } from '@rango-dev/whale';
import { Collapsible } from '@rango-dev/whale/lab';
import { Networks, useWallets, useWidget } from '@rango-dev/widget-embedded';
import { BigNumber } from 'bignumber.js';
import { ArrowRightLeft, CircleAlert, Copy, ExternalLink, KeyRound, RefreshCw } from 'lucide-react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { WIDGET_BASE_ROUTE } from '../../../constants/navigation';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { updateWalletDetailsItemState } from '../../../state/WalletDetailsSlice';
import {
  getBlockchainFullName,
  getBlockchainLogo,
  getTokenNetworkDesc,
} from '../../../utils/Constants';
import { areTokensEqual } from '../../../utils/generalFunctions/common';
import { numberToString } from '../../../utils/Numbers';
import { shortenUsername as shortenAddress } from '../../Profile/Leaderboard/Leaderboard.helpers';
import { SimpleNotification } from '../../SimpleNotification';
import { BlockchainSkeleton } from '../BlockchainSkeleton';
import { ExportPrivateKeyModal } from '../ExportPrivateKeyModal/ExportPrivateKeyModal';
import { getUsdValue, MIN_USD_VALUE, sortBalance, sortImg } from '../WalletDetails.helpers';

import { createWalletDetailsItemHash } from './WalletDetailsItem.helpers';
import styles from './WalletDetailsItem.module.css';

const ICON_SIZE = 16;
const ADDRESS_START_CHARS = 6;
const ADDRESS_END_CHARS = 4;
const UNKNOWN_TOKEN_LOGO = '/imgs/coins/unknown.png';
// Tickers longer than this are ellipsized and get a tooltip with the full name.
const TICKER_TOOLTIP_THRESHOLD = 7;

export function WalletDetailsItem(props: WalletDetailItemProps): ReactElement {
  const { walletDetailItem } = props;
  const { account, name, walletType } = walletDetailItem;
  const navigate = useNavigate();
  const { getWalletInfo } = useWallets();
  const {
    quote: { quoteInputs, updateQuoteInputs },
    wallets: { refetch },
  } = useWidget();
  const { exportEvmWallet, exportSolanaWallet } = useSocialWallet();
  const { hideSmallBalances, hideUnsupportedTokens, excludedWallets } = useAppSelector(
    (state) => state.walletDetails,
  );
  const walletDetailsItemsState = useAppSelector(
    (state) => state.walletDetails.walletDetailsItemsState,
  );
  const dispatch = useAppDispatch();
  const { blockchains: allBlockchain } = useAppSelector((state) => state.meta);
  const [copyNotifOpen, setCopyNotifOpen] = useState(false);
  const [showExportPrivateKeyModal, setShowExportPrivateKeyModal] = useState(false);
  const { pathname } = useLocation();

  const toggleExportPrivateKeyModal = () => setShowExportPrivateKeyModal((prevState) => !prevState);

  const sourceToken = quoteInputs.fromToken;
  const { t } = useTranslation();

  const filteredBalances = account.balances?.filter((balanceItem) => {
    if (hideUnsupportedTokens && !balanceItem?.isSupported) {
      return false;
    }

    if (hideSmallBalances && getUsdValue(balanceItem)?.lt(MIN_USD_VALUE)) {
      return false;
    }

    return true;
  });

  const sortedBalances = filteredBalances
    ?.sort((a, b) => sortBalance(a, b))
    ?.sort((a, b) => sortImg(a, b));
  const showEmptyState = !account.error && filteredBalances?.length === 0;
  const showPrimaryLoading = account.loading && (filteredBalances?.length === 0 || account.error);
  const showError = !showPrimaryLoading && account.error;
  const showTokenBalance = !showPrimaryLoading && !account.error;

  const currentItemState: undefined | boolean =
    walletDetailsItemsState[createWalletDetailsItemHash(name, account.address)]?.expanded;

  const isExpanded =
    typeof currentItemState === 'boolean'
      ? currentItemState
      : !(!showPrimaryLoading && showEmptyState);

  const setIsExpanded = (expanded: boolean) => {
    dispatch(
      updateWalletDetailsItemState({
        item: createWalletDetailsItemHash(name, account.address),
        state: { expanded },
      }),
    );
  };

  const walletNamesAndImages = walletType
    .map((w) => ({
      walletType: w,
      walletName: getWalletInfo(w).name,
      walletImg: getWalletInfo(w).img,
    }))
    .filter((wallet) => !excludedWallets.includes(wallet.walletType));

  function copyToClipboard(b: WalletDetailItemType) {
    void navigator.clipboard.writeText(b.account.address);
    setCopyNotifOpen(true);
  }

  const handleSwap = (balance: WalletBalance) => {
    const tokenFromBalance: Asset = {
      blockchain: balance.chain,
      address: balance.address,
      symbol: balance.symbol,
    };

    if (!areTokensEqual(tokenFromBalance, sourceToken)) {
      if (pathname !== WIDGET_BASE_ROUTE) {
        navigate(WIDGET_BASE_ROUTE);
      }
      updateQuoteInputs({
        fromBlockchain: tokenFromBalance.blockchain,
        fromToken: tokenFromBalance,
      });
    }
  };

  const reloadWallets = () => {
    const accounts = [
      {
        chain: name,
        address: account.address,
        walletType: walletNamesAndImages?.length ? walletNamesAndImages[0].walletType : '',
      },
    ];
    refetch(accounts || []);
  };

  const exportSocialWalletKey = () => {
    if (name === Networks.SOLANA) {
      void exportSolanaWallet();
    } else {
      void exportEvmWallet();
    }
  };

  function renderTokenRow(balance: WalletBalance): ReactElement {
    const usdValue = getUsdValue(balance);
    const { symbol } = balance;

    return (
      <Grid
        columns="4"
        key={balance.chain + balance.address}
        align="center"
        justify="between"
        gap="2">
        <Flex align="center" gap="2" minWidth="0" gridColumn="span 2">
          <img
            className={styles.tokenImage}
            alt={balance.ticker}
            src={balance.logo || UNKNOWN_TOKEN_LOGO}
            loading="lazy"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.src = UNKNOWN_TOKEN_LOGO;
            }}
          />
          <Flex direction="column" align="stretch" minWidth="0">
            {balance.ticker.length > TICKER_TOOLTIP_THRESHOLD ? (
              <Tooltip className={styles.tooltip} content={balance.ticker}>
                <Text size="3" weight="medium" align="left">
                  {`${balance.ticker.slice(0, TICKER_TOOLTIP_THRESHOLD)}…`}
                </Text>
              </Tooltip>
            ) : (
              <Text size="3" weight="medium" align="left">
                {balance.ticker}
              </Text>
            )}
            <Text size="2" color="gray" align="left" truncate>
              {getTokenNetworkDesc(balance.chain, balance.symbol, allBlockchain)}
            </Text>
          </Flex>
        </Flex>
        <Tooltip
          className={styles.tooltip}
          content={
            !!balance.usdPrice
              ? `1 ${balance.symbol} = ${numberToString(new BigNumber(balance.usdPrice))} USD`
              : t("Rango does not know {{balance}}'s price", { balance: balance.symbol })
          }>
          <Flex direction="column" align="start" gap={account.loading ? '1' : '0'}>
            {account.loading ? (
              <Fragment>
                <Skeleton width="56px" height="16px" />
                <Skeleton width="46px" height="12px" />
              </Fragment>
            ) : (
              <Fragment>
                <Text size="3" weight="medium">
                  {numberToString(new BigNumber(balance.amount))}
                </Text>
                {!!balance.usdPrice && (
                  <Text size="1" color="gray">
                    ${numberToString(usdValue)}
                  </Text>
                )}
              </Fragment>
            )}
          </Flex>
        </Tooltip>
        <Flex justify="end">
          <Tooltip className={styles.tooltip} content={t('Swap')}>
            <IconButton
              id={`wallet-details-modal-swap-${symbol}-icon-btn`}
              size="1"
              variant="ghost"
              color="gray"
              radius="full"
              aria-label={t('Swap') || 'Swap'}
              onClick={() => handleSwap(balance)}>
              <ArrowRightLeft size={ICON_SIZE} />
            </IconButton>
          </Tooltip>
        </Flex>
      </Grid>
    );
  }

  return (
    <>
      <ExportPrivateKeyModal
        open={showExportPrivateKeyModal}
        onClose={toggleExportPrivateKeyModal}
        onConfirm={() => {
          toggleExportPrivateKeyModal();
          exportSocialWalletKey();
        }}
      />
      <SimpleNotification
        open={copyNotifOpen}
        setOpen={setCopyNotifOpen}
        message="Wallet address copied!"
        severity="success"
      />
      <Collapsible.Root
        open={isExpanded}
        onOpenChange={setIsExpanded}
        variant="ghost"
        className={styles.card}>
        <Collapsible.Header p="3">
          <Flex align="center" gap="3" minWidth="0">
            <div className={styles.walletStack}>
              {walletNamesAndImages.map((wallet, index) => (
                <Tooltip
                  key={wallet.walletType}
                  className={styles.tooltip}
                  content={`${wallet.walletName} Wallet`}>
                  <div className={styles.walletSymbol}>
                    <img
                      className={styles.walletImage}
                      alt={wallet.walletName}
                      src={wallet.walletImg}
                    />
                    {index === walletNamesAndImages.length - 1 && (
                      <img
                        className={styles.chainBadge}
                        alt={name}
                        src={getBlockchainLogo(name, allBlockchain)}
                      />
                    )}
                  </div>
                </Tooltip>
              ))}
            </div>

            <Flex direction="column" align="start" minWidth="0">
              <Text size="4" weight="medium" truncate>
                {getBlockchainFullName(name, allBlockchain)}
              </Text>
              <Flex align="center" gap="1" minWidth="0">
                <Tooltip
                  className={styles.tooltip}
                  side="bottom"
                  align="center"
                  content={account.address}>
                  <Text size="2" color="gray" truncate>
                    {shortenAddress({
                      username: account.address,
                      startChars: ADDRESS_START_CHARS,
                      endChars: ADDRESS_END_CHARS,
                    })}
                  </Text>
                </Tooltip>
                <Tooltip className={styles.tooltip} content={t('Copy wallet address')}>
                  <IconButton
                    id="wallet-details-modal-copy-address-icon-btn"
                    size="1"
                    variant="ghost"
                    color="gray"
                    radius="full"
                    aria-label={t('Copy wallet address') || 'Copy wallet address'}
                    onClick={() => copyToClipboard(walletDetailItem)}>
                    <Copy size={ICON_SIZE} />
                  </IconButton>
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>

          <Flex align="start" gap="3">
            {walletType.includes(SOCIAL_WALLET_TYPE) && (
              <Tooltip className={styles.tooltip} content={t('Export private key')}>
                <IconButton
                  id="wallet-details-modal-export-private-key-icon-btn"
                  size="1"
                  variant="ghost"
                  color="gray"
                  radius="full"
                  aria-label={t('Export private key') || 'Export private key'}
                  onClick={toggleExportPrivateKeyModal}>
                  <KeyRound size={ICON_SIZE} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip className={styles.tooltip} content={t('Reload wallet')}>
              <IconButton
                id="wallet-details-modal-reload-wallet-icon-btn"
                size="1"
                variant="ghost"
                color="gray"
                radius="full"
                aria-label="reload"
                loading={account.loading}
                disabled={account.loading}
                onClick={reloadWallets}>
                <RefreshCw size={ICON_SIZE} />
              </IconButton>
            </Tooltip>

            {account.explorerUrl != null && (
              <Tooltip className={styles.tooltip} content={t('Go to account')}>
                <IconButton
                  id="wallet-details-modal-explorer-icon-btn"
                  size="1"
                  variant="ghost"
                  color="gray"
                  radius="full"
                  aria-label={t('Go to account') || 'Go to account'}
                  asChild>
                  <a href={account.explorerUrl} target="_blank" rel="noreferrer">
                    <ExternalLink size={ICON_SIZE} />
                  </a>
                </IconButton>
              </Tooltip>
            )}

            <Collapsible.Trigger
              id="wallet-details-modal-toggle-item-icon-btn"
              label={isExpanded ? t('Collapse') : t('Expand')}
            />
          </Flex>
        </Collapsible.Header>

        <Collapsible.Content p="3">
          <Flex direction="column" gap="3" p={!showError ? '3' : '0'} className={styles.tokenCard}>
            {showTokenBalance && sortedBalances?.map(renderTokenRow)}

            {!showPrimaryLoading && showEmptyState && (
              <Flex align="center" justify="center" py="2">
                <Text size="2" color="gray">
                  {t('No token in this blockchain')}
                </Text>
              </Flex>
            )}

            {showError && (
              <Callout.Root color="red">
                <Callout.Icon>
                  <CircleAlert size="16" />
                </Callout.Icon>
                <Callout.Text>
                  {t('Balances couldn’t be loaded at the moment. Please try again.')}
                </Callout.Text>
              </Callout.Root>
            )}

            {showPrimaryLoading && <BlockchainSkeleton />}
          </Flex>
        </Collapsible.Content>
      </Collapsible.Root>
    </>
  );
}
